import { Link } from 'react-router';
import { Button } from '@/components/ui/button';

/**
 * The public face of Maestro, served at `/` to anyone without a session.
 *
 * Why it lives here and not only in the Astro docs site: the docs deploy to
 * GitHub Pages on their own cadence, so a visitor hitting the app origin used to
 * land straight in the operator console via `<Navigate to="/chat">`. This is the
 * first thing a stranger sees on this origin.
 *
 * Copy is lifted from `packages/docs-web/src/content/docs/index.mdx` rather than
 * rewritten, so the two surfaces tell the same story. If the positioning changes,
 * change it in both.
 *
 * Deliberately built from plain elements plus `Button asChild` — no Radix
 * primitives. Radix portals render NOTHING under `renderToStaticMarkup`, which is
 * how this repo tests web components, so a portalled hero would silently assert
 * against an empty string. See DeployAssuranceModal.test.tsx for that trap.
 */

interface Pillar {
  readonly title: string;
  readonly body: string;
}

const PILLARS: readonly Pillar[] = [
  {
    title: 'Workflows as YAML',
    body: 'Package a delivery engagement as a versioned DAG. The same definition runs on your laptop, in CI, or against a remote agent mesh.',
  },
  {
    title: 'Governed context',
    body: 'Every run carries lineage: which outcome it serves, what it read, what it changed. Convergence scoring gates the work before it ships.',
  },
  {
    title: 'An orchestra of agents',
    body: 'Claude, Codex and the rest execute inside isolated worktrees with their own ports and credentials — conducted rather than improvised.',
  },
];

export function LandingPage(): React.ReactElement {
  return (
    <div className="min-h-screen overflow-y-auto bg-background text-text-primary">
      <header className="mx-auto flex w-full max-w-6xl items-center justify-between px-6 py-5">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary">
            <span className="text-sm font-semibold text-primary-foreground">M</span>
          </div>
          <span className="text-sm font-semibold text-text-primary">Maestro</span>
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/chat">Sign in</Link>
        </Button>
      </header>

      <main>
        <section className="relative overflow-hidden">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-[28rem] bg-[radial-gradient(circle_at_top,_var(--color-primary)_0%,_transparent_60%)] opacity-10"
          />
          <div className="relative mx-auto w-full max-w-4xl px-6 pb-20 pt-16 text-center md:pt-24">
            <p className="text-sm font-medium tracking-wide text-accent-bright">
              AI-orchestrated software delivery
            </p>
            <h1 className="mt-4 text-4xl font-semibold leading-tight tracking-tight text-text-primary md:text-6xl">
              One conductor.
              <br />
              An orchestra of AI agents.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-text-secondary md:text-lg">
              Package your delivery workflows as YAML and run them anywhere — building toward
              repo-native AI operating systems with governed context, lineage, and agent execution.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild size="lg">
                <Link to="/chat">Open Maestro</Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <a href="https://maestro.mahoosuc.ai/getting-started/installation/">Get started</a>
              </Button>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-6 pb-24">
          <div className="grid gap-4 md:grid-cols-3">
            {PILLARS.map(pillar => (
              <div
                key={pillar.title}
                className="rounded-xl border border-border bg-card p-6 text-left"
              >
                <h2 className="text-sm font-semibold text-text-primary">{pillar.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-text-secondary">{pillar.body}</p>
              </div>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto flex w-full max-w-6xl flex-col items-center justify-between gap-3 px-6 py-6 text-xs text-text-tertiary sm:flex-row">
          <span>Maestro — Mahoosuc Solutions</span>
          <a
            className="transition-colors hover:text-text-secondary"
            href="https://gitea.mahoosuc.ai/mahoosuc-solutions/Maestro"
          >
            Source
          </a>
        </div>
      </footer>
    </div>
  );
}
